import React, { useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

interface TimeSlotSelectorProps {
  durationData: any;       // The selected duration object from API
  dateData: any;           // The selected date's data from API
  onTimeSelect: (slotData: any) => void;
  selectedTimeSlot: any;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({ durationData, dateData, onTimeSelect, selectedTimeSlot }) => {
  const [hoveredSlot, setHoveredSlot] = useState<string | null>(null);

  // Get available time slots from the selected duration
  const timeSlots = useMemo(() => {
    if (!durationData?.available_time_slots) return [];
    return durationData.available_time_slots;
  }, [durationData]);

  // Check if there are any unavailable slots
  const unavailableSlots = useMemo(() => {
    return timeSlots.filter((slot: any) => !slot.available);
  }, [timeSlots]);

  const hasUnavailableSlots = unavailableSlots.length > 0;

  // Get the rental_slots and buffer_slots for the hovered/selected slot
  const getSlotVisualInfo = (slot: any) => {
    const rentalSlots = new Set<string>(slot.rental_slots || []);
    const bufferSlots = new Set<string>(slot.buffer_slots || []);
    return { rentalSlots, bufferSlots };
  };

  // Determine the currently active slot (hovered or selected) for visual highlighting
  const activeSlot = useMemo(() => {
    if (hoveredSlot) {
      return timeSlots.find((s: any) => s.time_24h === hoveredSlot);
    }
    if (selectedTimeSlot) {
      return timeSlots.find((s: any) => s.time_24h === selectedTimeSlot.time_24h);
    }
    return null;
  }, [hoveredSlot, selectedTimeSlot, timeSlots]);

  const activeVisualInfo = useMemo(() => {
    if (!activeSlot) return null;
    return getSlotVisualInfo(activeSlot);
  }, [activeSlot]);

  if (timeSlots.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography sx={{ color: '#999', fontSize: '14px' }}>
          No time slots available for this duration.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {hasUnavailableSlots && (
        <Box sx={{ 
          mb: 2, 
          p: 1.5, 
          backgroundColor: '#fff3e0', 
          borderRadius: '4px',
          border: '1px solid #ffb74d'
        }}>
          <Typography sx={{ fontSize: '14px', color: '#e65100', fontWeight: 500 }}>
            ⚠️ Some time slots are unavailable due to existing reservations or insufficient inventory.
          </Typography>
        </Box>
      )}
      <Box sx={styles.timeSlots}>
        {timeSlots.map((slot: any, index: number) => {
          const isAvailable = slot.available;
          const isSelected = selectedTimeSlot?.time_24h === slot.time_24h;
          const time12h = slot.time_12h || slot.time_24h;

          // Check if this slot's time_12h appears in the active slot's rental or buffer slots
          let slotHighlight: 'none' | 'rental' | 'buffer' = 'none';
          if (activeVisualInfo && !isSelected && isAvailable) {
            if (activeVisualInfo.bufferSlots.has(time12h)) {
              slotHighlight = 'buffer';
            } else if (activeVisualInfo.rentalSlots.has(time12h)) {
              slotHighlight = 'rental';
            }
          }

          let slotStyle = { ...styles.timeSlot };
          if (!isAvailable) {
            slotStyle = { ...slotStyle, ...styles.unavailableSlot };
          } else if (isSelected) {
            slotStyle = { ...slotStyle, ...styles.selectedTimeSlot };
          } else if (slotHighlight === 'buffer') {
            slotStyle = { ...slotStyle, ...styles.bufferSlot };
          } else if (slotHighlight === 'rental') {
            slotStyle = { ...slotStyle, ...styles.rentalSlot };
          }

          return (
            <Button
              key={slot.time_24h || index}
              onClick={() => isAvailable && onTimeSelect(slot)}
              onMouseEnter={() => isAvailable && setHoveredSlot(slot.time_24h)}
              onMouseLeave={() => setHoveredSlot(null)}
              disabled={!isAvailable}
              sx={slotStyle}
            >
              {time12h}
            </Button>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: '#2196F3' }} />
          <Typography sx={{ fontSize: '12px', color: '#666' }}>Selected</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: '#9e9e9e' }} />
          <Typography sx={{ fontSize: '12px', color: '#666' }}>Rental Period</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: '#ff5252' }} />
          <Typography sx={{ fontSize: '12px', color: '#666' }}>Buffer/Cleaning</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: '#e0e0e0' }} />
          <Typography sx={{ fontSize: '12px', color: '#666' }}>Unavailable</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  timeSlots: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  timeSlot: {
    padding: "2px 4px",
    borderRadius: "4px",
    border: "2px solid #2196F3",
    backgroundColor: "#ffffff",
    color: "#2196F3",
    fontSize: "16px",
    fontWeight: 500,
    minWidth: "85px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f0f8ff",
    },
  },
  selectedTimeSlot: {
    backgroundColor: "#2196F3 !important",
    color: "#ffffff !important",
    borderColor: "#2196F3 !important",
    "&:hover": {
      backgroundColor: "#1976D2 !important",
    },
  },
  rentalSlot: {
    backgroundColor: "#9e9e9e !important",
    color: "#ffffff !important",
    borderColor: "#9e9e9e !important",
    "&:hover": {
      backgroundColor: "#757575 !important",
    },
  },
  bufferSlot: {
    backgroundColor: "#ff5252 !important",
    color: "#ffffff !important",
    borderColor: "#ff5252 !important",
    "&:hover": {
      backgroundColor: "#ff1744 !important",
    },
  },
  unavailableSlot: {
    backgroundColor: "#e0e0e0 !important",
    color: "#9e9e9e !important",
    borderColor: "#e0e0e0 !important",
    cursor: "not-allowed !important",
    "&:hover": {
      backgroundColor: "#e0e0e0 !important",
    },
  },
};

export default TimeSlotSelector;
